import { type ReactElement, useState } from 'react';
import { type GuestQuoteLinkCopy, useGuestQuoteLink } from '@/entities/quote';
import { Button, Input } from '@/shared/ui';

export interface GuestQuoteLinkButtonProps {
  readonly quoteRequestId: string;
  /** 회원 계정에 연결된 견적 — 링크를 열면 내용 없이 "회원 계정에 연결된 견적" 안내만 보인다(비회원 견적 설계 G9). */
  readonly claimed: boolean;
}

const LINK_NOTICE = '고객이 이 링크로 견적을 확인하고 주문합니다. 카카오톡·문자로 보낼 때 씁니다.';
const CLAIMED_NOTICE = '회원 계정에 연결된 견적입니다. 링크를 열면 로그인 안내만 보입니다.';
const COPIED_NOTICE = '견적 링크를 복사했습니다.';
const CLIPBOARD_FAILED_NOTICE = '클립보드에 복사하지 못했습니다. 아래 링크를 직접 복사해 주세요.';

/**
 * 비회원 견적 [링크 복사](비회원 견적 설계 G14) — 누를 때마다 서버에서 링크를 받아 클립보드에 쓴다.
 * 클립보드를 쓸 수 없는 브라우저면 읽기 전용 칸에 링크를 보여 줘 직접 복사하게 한다. 링크는 이 컴포넌트 state 에만 둔다.
 */
export const GuestQuoteLinkButton = ({
  quoteRequestId,
  claimed,
}: GuestQuoteLinkButtonProps): ReactElement => {
  const [copied, setCopied] = useState<GuestQuoteLinkCopy | null>(null);
  const { copy, isPending, error } = useGuestQuoteLink();

  const handleCopy = async (): Promise<void> => {
    setCopied(null);
    const result = await copy(quoteRequestId);

    if (result !== undefined) {
      setCopied(result);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          isLoading={isPending}
          onClick={() => {
            void handleCopy();
          }}
        >
          링크 복사
        </Button>
        <span className="text-xs text-muted-foreground">
          {claimed ? CLAIMED_NOTICE : LINK_NOTICE}
        </span>
      </div>
      {copied?.status === 'COPIED' ? (
        <p role="status" className="text-sm text-emerald-700">
          {COPIED_NOTICE}
        </p>
      ) : null}
      {copied?.status === 'CLIPBOARD_FAILED' ? (
        <div role="alert" className="flex flex-col gap-1 text-sm text-destructive">
          <p>{CLIPBOARD_FAILED_NOTICE}</p>
          <Input
            readOnly
            aria-label="견적 링크"
            value={copied.url}
            onFocus={(event) => event.currentTarget.select()}
          />
        </div>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
};
